import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { categories } from "@/constants/categories";
import { BUYER_FORM_URL, VENDOR_FORM_URL } from "@/constants/links";
import { MaskedLine, FadeIn } from "@/components/Reveal";

export default function Marketplace() {
  return (
    <>
      <section data-testid="marketplace-page" className="relative overflow-hidden pb-14 pt-32 sm:pt-40 lg:pt-48">
        <div className="pointer-events-none absolute -top-24 right-[-8%] h-[26rem] w-[26rem] rounded-full bg-[#1E3A8A]/10 blur-[110px]" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <p className="eyebrow flex items-center gap-3" data-testid="marketplace-eyebrow">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#1E3A8A]" />
            Marketplace
          </p>
          <h1 className="mt-8 font-sans text-4xl font-bold leading-[1.08] tracking-tight text-[#0F172A] sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.15}>Five launch categories.</MaskedLine>
            <MaskedLine delay={0.3} className="text-gold-gradient">Coming soon.</MaskedLine>
          </h1>
          <FadeIn delay={0.55}>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-[#64748B] sm:text-lg">
              The Aflimart marketplace opens with five carefully chosen categories. The shelves are
              being set — join the waitlist to shop them first.
            </p>
            <span className="mt-8 inline-flex items-center gap-2 rounded-full border border-[#1E3A8A]/40 bg-[#1E3A8A]/10 px-5 py-2 font-sans text-[11px] tracking-[0.25em] text-[#1E3A8A]" data-testid="marketplace-coming-soon">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#1E3A8A]" /> COMING SOON
            </span>
          </FadeIn>
        </div>
      </section>

      <section data-testid="marketplace-categories" className="mx-auto max-w-7xl px-4 pb-16 sm:px-8 lg:px-16">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-12 lg:gap-8">
          {categories.map((c, i) => (
            <FadeIn key={c.name} delay={0.06 * i} className={c.span}>
              <Link to={`/categories/${c.slug}`} data-testid={`marketplace-card-${c.slug}`} className="card-dark group block h-full overflow-hidden hover:-translate-y-1">
                <div className="relative h-44 overflow-hidden sm:h-52">
                  <img
                    src={c.img}
                    alt={c.alt}
                    className="h-full w-full object-cover grayscale-[30%] sepia-[15%] contrast-[1.05] transition-transform duration-700 group-hover:scale-[1.04]"
                    loading="lazy"
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0F172A] via-transparent to-transparent" />
                  <span className="absolute right-5 top-5 font-sans text-[10px] tracking-[0.25em] text-[#64748B]">PLANNED FOR LAUNCH</span>
                </div>
                <div className="p-8 sm:p-10">
                  <span className="flex h-12 w-12 rotate-45 items-center justify-center rounded-lg border border-[#1E3A8A]/40 transition-colors duration-500 group-hover:border-[#1E3A8A] group-hover:bg-[#1E3A8A]/10">
                    <c.icon size={20} className="-rotate-45 text-[#1E3A8A]" />
                  </span>
                  <h3 className="mt-8 font-sans text-xl text-[#0F172A] sm:text-2xl">{c.name}</h3>
                  <p className="mt-3 max-w-md text-sm leading-relaxed text-[#64748B]">{c.copy}</p>
                  <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-[#1E3A8A]">
                    Preview this category
                    <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                  </span>
                </div>
              </Link>
            </FadeIn>
          ))}
        </div>
      </section>

      <section data-testid="marketplace-cta" className="border-t border-[#1E3A8A]/10 bg-[#F2F4F7]">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 sm:py-24 lg:px-16">
          <FadeIn>
            <h2 className="font-sans text-3xl tracking-tight text-[#0F172A] sm:text-4xl">Be there when the doors open.</h2>
            <div className="mt-10 flex flex-wrap gap-4">
              <a href={BUYER_FORM_URL} target="_blank" rel="noopener noreferrer" data-testid="marketplace-cta-buyer" className="btn-gold">
                Join the Buyer Waitlist <ArrowUpRight size={16} />
              </a>
              <a href={VENDOR_FORM_URL} target="_blank" rel="noopener noreferrer" data-testid="marketplace-cta-vendor" className="btn-ghost">
                Become a Vendor
              </a>
            </div>
          </FadeIn>
        </div>
      </section>
    </>
  );
}
